import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Scale, Award, Eye, Diamond, Target, Shield,  BookOpen, Star, Trophy, CheckCircle, Lightbulb, Phone } from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { Link } from 'react-router-dom';
import AnimatedNetworkBackground from '../components/AnimatedNetworkBackground';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutUsPage = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeValue, setActiveValue] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id^="section-"]').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const values = [
    { 
      icon: <Eye className="w-6 h-6 sm:w-8 sm:h-8" />, 
      title: "Transparency", 
      desc: "We believe in complete clarity and honesty in all our dealings with clients, providing information clearly and understandably",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />, 
      title: "Trust", 
      desc: "Building long-term relationships with our clients based on mutual trust and credibility in work",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8" />, 
      title: "Creativity", 
      desc: "We provide innovative and unconventional legal solutions that uniquely suit each client's needs",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Award className="w-6 h-6 sm:w-8 sm:h-8" />, 
      title: "Professionalism", 
      desc: "Commitment to the highest standards of legal service and continuous development of our skills and expertise",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8" />, 
      title: "Protection", 
      desc: "Protecting our clients' rights and interests with full strength and dedication, defending them with the best legal methods",
      color: "from-[#0A3A23] to-[#0A3A23]"
    },
    { 
      icon: <Diamond className="w-6 h-6 sm:w-8 sm:h-8" />, 
      title: "Adaptability", 
      desc: "Keeping up with modern legal developments and adapting to the requirements of the era and market challenges",
      color: "from-[#0A3A23] to-[#0A3A23]"
    }
  ];

  const timeline = [
    { year: "1", event: "Step Towards Security", desc: "Listening with attention" },
    { year: "2", event: "Step Towards Clarity", desc: "Holding a meeting with you" },
    { year: "3", event: "Step Towards Goals", desc: "Setting work strategies" },
    { year: "4", event: "Step Towards Success", desc: "Signing the partnership contract" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100" dir="ltr">
      
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <section id="section-hero" className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-all duration-1000 mt-16 sm:mt-20 ${isVisible['section-hero'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`} style={{ backgroundColor: '#0A3A23' }}>
        
        {/* Background Decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-10 sm:top-20 left-10 sm:left-20 w-16 h-16 sm:w-32 sm:h-32 bg-gradient-to-br from-emerald-400/20 to-emerald-600/20 rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 right-10 sm:right-20 w-24 h-24 sm:w-48 sm:h-48 bg-gradient-to-br from-emerald-300/10 to-emerald-500/10 rounded-full animate-bounce" style={{animationDuration: '4s'}}></div>
          <div className="absolute top-1/3 right-1/4 w-10 h-10 sm:w-20 sm:h-20 bg-gradient-to-br from-emerald-400/20 to-emerald-600/20 rounded-full animate-ping" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            {/* Content - Now on the Left */}
            <div className="text-left order-2 lg:order-1">
              <div className="mb-6 sm:mb-8">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                  Who We Are
                </h1>
                <div className="w-20 sm:w-32 h-1 sm:h-2 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"></div>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-4">
                <Scale className="w-8 h-8 sm:w-12 sm:h-12 text-emerald-300" />
                <span className="text-lg sm:text-2xl md:text-3xl lg:text-4xl">Anas Abdul Rahman Khayyat Company</span>
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-white/20">
                <p className="text-base sm:text-lg md:text-xl text-white/80 leading-relaxed">
                  We provide specialized and professional legal services in the Kingdom of Saudi Arabia, where we are committed to providing the best legal services to our clients according to the highest professional and legal standards.
                </p>
              </div>
            </div>

            {/* Lawyer Image - Now on the Right */}
            <div className="flex justify-center order-1 lg:order-2">
              <div className="relative">
                <div className="w-64 h-80 sm:w-80 sm:h-96 lg:w-96 lg:h-[500px] bg-gradient-to-br from-emerald-400/30 to-emerald-600/30 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl flex items-center justify-center border-4 border-emerald-400/30 overflow-hidden">
                  <img 
                    src="/images/صور الموقع (1)/0.jpg" 
                    alt="Anas Law Company"
                    className="w-full h-full object-cover rounded-xl sm:rounded-2xl"
                  />
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-3 sm:-top-6 -right-3 sm:-right-6 w-8 h-8 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full animate-spin shadow-xl" style={{animationDuration: '10s'}}></div>
                <div className="absolute -bottom-2 sm:-bottom-4 -left-2 sm:-left-4 w-6 h-6 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full animate-pulse shadow-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Story Section */}
      <section id="section-story" className={`py-8 sm:py-12 md:py-16 bg-white relative transition-all duration-1000 delay-300 ${isVisible['section-story'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-start">
            
            {/* Office Image - Now on the Left */}
            <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
              {/* Main Office Image */}
              <div className="relative">
                <div className="w-full bg-gradient-to-br from-[#0a3a23]/10 to-[#0a3a23]/20 rounded-2xl sm:rounded-3xl shadow-xl flex items-center justify-center border-4 border-[#0a3a23]/20 overflow-hidden">
                  <img 
                    src="/images/صور الموقع (1)/39.jpg" 
                    alt="Main Office Photo"
                    className="w-full h-auto object-contain rounded-xl sm:rounded-2xl"
                  />
                </div>
                <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 w-8 h-8 sm:w-12 sm:h-12 bg-[#0a3a23]/20 rounded-full animate-bounce shadow-lg"></div>
              </div>
            </div>

            {/* Story Content - Now on the Right */}
            <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
              <div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-4">
                  <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-[#0a3a23]" />
                  Our Story
                </h2>
                <div className="w-16 sm:w-24 h-1 rounded-full mb-6 sm:mb-8" style={{ backgroundColor: '#0A3A23' }}></div>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <div className="bg-gradient-to-r from-[#0a3a23]/5 to-[#0a3a23]/10 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border-l-4 border-[#0a3a23]/30 shadow-lg">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-[#0a3a23]" />
                    The Beginning
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                    We began providing our comprehensive legal services in the Kingdom of Saudi Arabia, offering effective legal support to both public and private sectors. At Anas Abdul Rahman Khayyat Company, we are committed to providing practical and reliable legal solutions through a specialized, highly efficient team.
                  </p>
              </div>

                <div className="bg-gradient-to-r from-[#0a3a23]/5 to-[#0a3a23]/10 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border-l-4 border-[#0a3a23]/30 shadow-lg">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                    <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-[#0a3a23]" />
                    Our Mission
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                    We focus on providing a wide range of legal services including consultations, contract drafting, and client representation before judicial authorities. We also ensure providing integrated legal services for individuals before general courts, personal status courts, and labor courts.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-[#0a3a23]/5 to-[#0a3a23]/10 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border-l-4 border-[#0a3a23]/30 shadow-lg">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                    <Diamond className="w-5 h-5 sm:w-6 sm:h-6 text-[#0a3a23]" />
                    Our Vision
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                    In addition to providing alternative solutions for dispute resolution in line with Saudi regulations, while maintaining the highest standards of professionalism and integrity in all our services.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="section-values" className={`py-8 sm:py-12 md:py-16 bg-white relative transition-all duration-1000 delay-600 ${isVisible['section-values'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-[#0a3a23]/10 to-[#0a3a23]/20 text-[#0a3a23] px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Award className="w-4 h-4 sm:w-5 sm:h-5" />
              Our Core Values
            </div>
            <div className="w-20 sm:w-32 h-1 mx-auto rounded-full mb-4 sm:mb-6" style={{ backgroundColor: '#0A3A23' }}></div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4 sm:px-0">
              We believe in a set of core values that guide our work and determine how we deal with our clients and partners
            </p>
          </div>

          {/* Clean Values Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {values.map((value, index) => (
              <div key={index} className="group">
                <div className="bg-gradient-to-br from-[#0a3a23]/5 to-[#0a3a23]/10 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-[#0a3a23]/20 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${value.color} rounded-xl sm:rounded-2xl mx-auto mb-4 sm:mb-6 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {value.icon}
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 group-hover:text-[#0A3A23] transition-colors">{value.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{value.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Simple Central Element */}
          <div className="text-center mt-8 sm:mt-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full mx-auto flex items-center justify-center shadow-2xl" style={{ backgroundColor: '#0A3A23' }}>
              <Star className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
            </div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mt-4 sm:mt-6">Excellence in Legal Work</h3>
            <p className="text-sm sm:text-base text-gray-600 mt-2 sm:mt-4 max-w-2xl mx-auto px-4 sm:px-0">
              We are committed to the highest standards of quality and professionalism in all our legal services
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="section-why-choose-us" className={`py-8 sm:py-12 md:py-16 relative overflow-hidden transition-all duration-1000 delay-900 ${isVisible['section-why-choose-us'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`} style={{ backgroundColor: '#0A3A23' }}>
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 sm:top-20 left-10 sm:left-20 w-16 h-16 sm:w-32 sm:h-32 border border-white/10 rounded-full"></div>
          <div className="absolute bottom-10 sm:bottom-20 right-10 sm:right-20 w-24 h-24 sm:w-48 sm:h-48 border border-white/10 rounded-full"></div>
          <div className="absolute top-1/3 right-1/4 w-10 h-10 sm:w-20 sm:h-20 border border-white/10 rounded-full"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Why Choose Us Title */}
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">Why Choose Us?</h2>
          </div>

          {/* Call to Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8">
            <a
              href="https://wa.me/966115115945?text=I want a legal consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] hover:from-[#0a3a23] hover:to-[#0a3a23] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Get Consultation
            </a>
            <a
              href="/images/about/أنس الخياط - ملف تعريفي-5 (1).pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] hover:from-[#0a3a23] hover:to-[#0a3a23] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Company Profile
            </a>
          </div>

          {/* Divider */}
          <div className="w-full border-t-2 border-dashed border-yellow-400 mb-6 sm:mb-8"></div>
          
          {/* Scales of Justice Icon */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <Scale className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-yellow-400" />
          </div>

          {/* Why Choose Us Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border-2 border-yellow-400">
                <Shield className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-yellow-400" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3">We Think Like a Client</h3>
              <p className="text-white/80 text-xs sm:text-sm leading-relaxed">
                We work together step by step, exhausting all legal avenues to solve your problem, no matter how complex, to get the best possible solutions.
                </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border-2 border-yellow-400">
                <Users className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-yellow-400" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3">We Work as Partners</h3>
              <p className="text-white/80 text-xs sm:text-sm leading-relaxed">
                We believe that you are a true partner to us, and we work side by side to achieve your goals, because your success is our success.
                </p>
            </div>

            <div className="text-center sm:col-span-2 md:col-span-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border-2 border-yellow-400">
                <Lightbulb className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-yellow-400" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3">We Preserve Your Privacy</h3>
              <p className="text-white/80 text-xs sm:text-sm leading-relaxed">
                Your privacy is a priority, and your information is in safe hands. We are committed to protecting it with full professionalism within the framework of the legal relationship that unites us.
                </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="section-timeline" className={`py-8 sm:py-12 md:py-16 bg-gradient-to-br from-gray-50 to-white transition-all duration-1000 delay-900 ${isVisible['section-timeline'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center justify-center gap-2 sm:gap-4">
              <Trophy className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-emerald-600" />
              Our Journey Together
            </h2>
            <div className="w-20 sm:w-32 h-1 mx-auto rounded-full mb-4 sm:mb-6" style={{ backgroundColor: '#0A3A23' }}></div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4 sm:px-0">
              We believe that our journey with each of our partners will be unique, and we are keen to provide distinguished legal services that achieve success for them
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line - Hidden on mobile */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 rounded-full" style={{ backgroundColor: '#0A3A23' }}></div>

            {/* Desktop Timeline */}
            <div className="hidden lg:block space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-16' : 'pl-16'}`}>
                    <div className={`bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 hover:shadow-3xl transition-all duration-500 hover:scale-105`} style={{ borderColor: index % 2 === 0 ? '#0A3A23' : '#E5E7EB' }}>
                      <div className={`text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 flex items-center gap-3 sm:gap-4 ${index % 2 === 0 ? '' : 'text-gray-600'}`} style={{ color: index % 2 === 0 ? '#0A3A23' : undefined }}>
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${index % 2 === 0 ? 'from-[#0A3A23] to-[#0A3A23]' : 'from-gray-400 to-gray-500'} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg`}>
                          <Star className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <span>{item.event}</span>
                      </div>
                      <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">{item.desc}</p>
                      
                      {/* Decorative Elements */}
                      <div className="mt-4 sm:mt-6 flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 bg-emerald-600 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Timeline Node with Beautiful Image */}
                  <div className="relative z-10">
                    <div className="relative">
                      {/* Glowing Effect */}
                      <div className={`absolute inset-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full blur-xl opacity-30 animate-pulse`} style={{ backgroundColor: index % 2 === 0 ? '#0A3A23' : '#6B7280' }}></div>
                      
                      {/* Main Icon Container */}
                      <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-white shadow-2xl relative z-10 bg-gradient-to-br ${index % 2 === 0 ? 'from-[#0A3A23] to-[#0A3A23]' : 'from-gray-400 to-gray-500'} flex items-center justify-center`}>
                        <Star className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                      </div>
                      
                      {/* Floating Elements */}
                      <div className={`absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full animate-bounce shadow-lg`} style={{animationDuration: '2s'}}></div>
                      <div className={`absolute -bottom-1 sm:-bottom-2 -left-1 sm:-left-2 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-emerald-300 to-emerald-500 rounded-full animate-ping`} style={{animationDuration: '3s'}}></div>
                    </div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>

            {/* Mobile Timeline - Vertical Stack */}
            <div className="lg:hidden space-y-6">
              {timeline.map((item, index) => (
                <div key={index} className="relative">
                  {/* Mobile Timeline Node */}
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      {/* Glowing Effect */}
                      <div className="absolute inset-0 w-12 h-12 sm:w-16 sm:h-16 rounded-full blur-xl opacity-30 animate-pulse" style={{ backgroundColor: '#0A3A23' }}></div>
                      
                      {/* Main Icon Container */}
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-white shadow-2xl relative z-10 bg-gradient-to-br from-[#0A3A23] to-[#0A3A23] flex items-center justify-center">
                        <Star className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                      </div>
                      
                      {/* Step Number */}
                      <div className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-white border-2 border-[#0A3A23] rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-xs sm:text-sm font-bold text-[#0A3A23]">{item.year}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mobile Content Card */}
                  <div className="bg-gradient-to-br from-white to-gray-50 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-[#0A3A23]/20 mx-2 sm:mx-4">
                    <div className="text-center">
                      <h3 className="text-lg sm:text-xl font-bold text-[#0A3A23] mb-2 sm:mb-3">{item.event}</h3>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{item.desc}</p>
                      
                      {/* Decorative Elements */}
                      <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 bg-emerald-600 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Floating Contact Buttons */}
      <a
        href="tel:920033053"
        className="fixed bottom-16 sm:bottom-20 md:bottom-28 left-3 sm:left-4 md:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-2 sm:p-3 md:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <Phone className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
      </a>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/966115115945?text=I want a legal consultation"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-3 sm:bottom-4 md:bottom-6 left-3 sm:left-4 md:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-2 sm:p-3 md:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <WhatsAppIcon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
      </a>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AboutUsPage; 
